import { memo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { keyframes } from 'styled-components'
import { calculateTime, toTimeString } from '../../../../utils/hoursAndMinutes'
import { cloneDeep } from 'lodash'
import { Spinner } from 'react-bootstrap'

/* ── Animations ──────────────────────────────────────────────────────────── */
const slideIn = keyframes`
  from { opacity: 0; transform: scale(0.94); }
  to   { opacity: 1; transform: scale(1); }
`

/* ── Outer wrapper ───────────────────────────────────────────────────────── */
const Wrapper = styled.div`
  background: #ffffff;
  border: 1px solid #ebebeb;
  border-radius: 14px;
  overflow: hidden;
  max-width: 480px;
  width: 100%;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem 0.85rem;
  border-bottom: 1px solid #f0f0f0;
`

const HeaderTitle = styled.h2`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #aaa;
  margin: 0;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SlotBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: #dc3545;
  border-radius: 20px;
  padding: 2px 9px;
  letter-spacing: 0.01em;
`

const ActionBtn = styled.button`
  font-size: 11px;
  font-weight: 700;
  border-radius: 20px;
  padding: 2px 10px;
  border: 1px solid;
  cursor: pointer;
  letter-spacing: 0.01em;
  transition:
    background 0.12s,
    color 0.12s;

  ${({ $variant }) =>
    $variant === 'danger'
      ? `
    background: #dc3545;
    border-color: #dc3545;
    color: #fff;
    &:hover { background: #c82333; border-color: #c82333; }
  `
      : $variant === 'cancel'
        ? `
    background: #fff;
    border-color: #ddd;
    color: #888;
    &:hover { background: #f5f5f5; }
  `
        : `
    background: #fff;
    border-color: #ddd;
    color: #555;
    &:hover { background: #f5f5f5; }
  `}
`

/* ── Hour rows ───────────────────────────────────────────────────────────── */
const HourRow = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 0 1.25rem;
  min-height: 40px;
  border-bottom: 1px solid #f7f7f7;
  transition: background 0.12s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafafa;
  }
`

const HourLabel = styled.div`
  width: 48px;
  flex-shrink: 0;
  padding-top: 12px;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ $hasSlots }) => ($hasSlots ? '#1a1a1a' : '#c8c8c8')};
  transition: color 0.15s;
`

/* ── Thin vertical track + dot ───────────────────────────────────────────── */
const Track = styled.div`
  position: relative;
  width: 1px;
  margin: 0 14px;
  flex-shrink: 0;
  align-self: stretch;
  background: #e8e8e8;
  min-height: 40px;
`

const TrackDot = styled.div`
  position: absolute;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  width: ${({ $hasSlots }) => ($hasSlots ? '9px' : '5px')};
  height: ${({ $hasSlots }) => ($hasSlots ? '9px' : '5px')};
  border-radius: 50%;
  background: ${({ $hasSlots }) => ($hasSlots ? '#dc3545' : '#e0e0e0')};
  border: ${({ $hasSlots }) =>
    $hasSlots ? '2px solid #fff' : '1.5px solid #e0e0e0'};
  transition: all 0.2s ease;
`

/* ── Chip slots ──────────────────────────────────────────────────────────── */
const SlotsArea = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding: 7px 0;
  min-height: 40px;
  align-items: center;
`

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${({ $selected }) => ($selected ? '#3b82f6' : '#a6e144')};
  border: 1px solid ${({ $selected }) => ($selected ? '#2563eb' : '#8fcc33')};
  border-radius: 8px;
  padding: 3px 8px 3px 10px;
  font-size: 12px;
  font-weight: 700;
  color: ${({ $selected }) => ($selected ? '#fff' : '#2d4a0e')};
  white-space: nowrap;
  animation: ${slideIn} 0.18s ease both;
  transition:
    background 0.12s,
    border-color 0.12s,
    color 0.12s;
  cursor: ${({ $selectionMode }) => ($selectionMode ? 'pointer' : 'default')};

  &:hover {
    background: ${({ $selected }) => ($selected ? '#2563eb' : '#97d438')};
  }
`

const DeleteBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: none;
  background: rgba(45, 74, 14, 0.18);
  cursor: pointer;
  padding: 0;
  transition: background 0.12s;
  flex-shrink: 0;

  &:hover {
    background: rgba(45, 74, 14, 0.35);
  }

  svg {
    width: 7px;
    height: 7px;
  }
`

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.4rem;
  padding: 2rem 1.5rem;
  color: #ccc;
  font-size: 12px;
  font-weight: 500;
`

/* ── Icons ───────────────────────────────────────────────────────────────── */
const CloseIcon = () => (
  <svg
    viewBox='0 0 8 8'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
  >
    <line x1='1.5' y1='1.5' x2='6.5' y2='6.5' />
    <line x1='6.5' y1='1.5' x2='1.5' y2='6.5' />
  </svg>
)

const CheckIcon = () => (
  <svg
    viewBox='0 0 8 8'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.5'
    strokeLinecap='round'
    strokeLinejoin='round'
    style={{ width: 9, height: 9 }}
  >
    <polyline points='1,4 3.2,6.5 7,1.5' />
  </svg>
)

/* ── TimeSlot chip ───────────────────────────────────────────────────────── */
const TimeSlot = memo(
  ({
    obj,
    index,
    durationInMinutes,
    hours,
    updateTimeSlots,
    selectionMode,
    isSelected,
    onToggleSelect,
  }) => {
    const [isDeleting, setIsDeleting] = useState(false)
    const end = calculateTime('plus', obj.hour, obj.minute, durationInMinutes)
    const slotKey = `${obj.hour}-${index}`

    const handleDelete = useCallback(() => {
      setIsDeleting(true)
      const newHours = cloneDeep(hours)
      newHours[obj.hour].splice(index, 1)
      updateTimeSlots(newHours)
    }, [hours, obj.hour, index, updateTimeSlots])

    return (
      <Chip
        $selected={isSelected}
        $selectionMode={selectionMode}
        onClick={selectionMode ? () => onToggleSelect(slotKey) : undefined}
      >
        {toTimeString(obj.hour, obj.minute)}
        <span style={{ opacity: 0.45, fontWeight: 400 }}>–</span>
        {toTimeString(end[0], end[1])}
        {selectionMode ? (
          isSelected && <CheckIcon />
        ) : isDeleting ? (
          <Spinner
            size='sm'
            animation='border'
            style={{ width: 11, height: 11, borderWidth: 2, color: '#2d4a0e' }}
          />
        ) : (
          <DeleteBtn onClick={handleDelete} title='Remove slot'>
            <CloseIcon />
          </DeleteBtn>
        )}
      </Chip>
    )
  },
)

/* ── HourBlock ───────────────────────────────────────────────────────────── */
const HourBlock = memo(
  ({
    times,
    hour,
    durationInMinutes,
    hours,
    updateTimeSlots,
    selectionMode,
    selected,
    onToggleSelect,
  }) => {
    const hasSlots = times.length > 0

    return (
      <HourRow>
        <HourLabel $hasSlots={hasSlots}>{toTimeString(hour, 0)}</HourLabel>
        <Track>
          <TrackDot $hasSlots={hasSlots} />
        </Track>
        <SlotsArea>
          {times.map((obj, index) => (
            <TimeSlot
              key={`${obj.hour}-${obj.minute}-${index}`}
              obj={obj}
              index={index}
              durationInMinutes={durationInMinutes}
              hours={hours}
              updateTimeSlots={updateTimeSlots}
              selectionMode={selectionMode}
              isSelected={selected.has(`${obj.hour}-${index}`)}
              onToggleSelect={onToggleSelect}
            />
          ))}
        </SlotsArea>
      </HourRow>
    )
  },
)

/* ── Main ────────────────────────────────────────────────────────────────── */
const TimesList = memo(({ durationInMinutes, hours, updateTimeSlots }) => {
  const { t } = useTranslation()
  const [selectionMode, setSelectionMode] = useState(false)
  const [selected, setSelected] = useState(new Set())
  const [showAll, setShowAll] = useState(false)

  const totalSlots = hours.reduce((sum, h) => sum + h.length, 0)
  const visibleHours = hours
    .map((times, i) => ({ times, i }))
    .filter(({ times }) => showAll || times.length > 0)

  const toggleSelect = useCallback((key) => {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }, [])

  const cancelSelection = useCallback(() => {
    setSelectionMode(false)
    setSelected(new Set())
  }, [])

  const deleteSelected = useCallback(() => {
    const toDelete = {}
    selected.forEach((key) => {
      const [h, i] = key.split('-').map(Number)
      if (!toDelete[h]) toDelete[h] = []
      toDelete[h].push(i)
    })
    const newHours = cloneDeep(hours)
    Object.entries(toDelete).forEach(([h, indices]) => {
      indices.sort((a, b) => b - a)
      indices.forEach((i) => newHours[h].splice(i, 1))
    })
    updateTimeSlots(newHours)
    cancelSelection()
  }, [hours, selected, updateTimeSlots, cancelSelection])

  return (
    <Wrapper>
      <Header>
        <HeaderTitle>{t('appointment:appointments')}</HeaderTitle>
        <HeaderActions>
          {selected.size > 0 && (
            <ActionBtn $variant='danger' onClick={deleteSelected}>
              {t('delete')} {selected.size}
            </ActionBtn>
          )}
          {totalSlots > 0 && !selectionMode && (
            <>
              <SlotBadge>
                {totalSlots} {totalSlots === 1 ? 'slot' : 'slots'}
              </SlotBadge>
              <ActionBtn onClick={() => setShowAll((v) => !v)}>
                {showAll
                  ? t('appointment:activeHoursOnly')
                  : t('appointment:showAllHours')}
              </ActionBtn>
              <ActionBtn onClick={() => setSelectionMode(true)}>
                {t('select')}
              </ActionBtn>
            </>
          )}
          {selectionMode && (
            <ActionBtn $variant='cancel' onClick={cancelSelection}>
              {t('appointment:cancel')}
            </ActionBtn>
          )}
        </HeaderActions>
      </Header>

      {totalSlots === 0 && (
        <EmptyState>
          <svg
            width='26'
            height='26'
            viewBox='0 0 24 24'
            fill='none'
            stroke='#ddd'
            strokeWidth='1.5'
          >
            <rect x='3' y='4' width='18' height='17' rx='2' />
            <line x1='3' y1='9' x2='21' y2='9' />
            <line x1='8' y1='2' x2='8' y2='6' />
            <line x1='16' y1='2' x2='16' y2='6' />
          </svg>
          <span>{t('appointment:noAppointments')}</span>
        </EmptyState>
      )}

      {visibleHours.map(({ times, i }) => (
        <HourBlock
          key={i}
          times={times}
          hour={i}
          durationInMinutes={durationInMinutes}
          hours={hours}
          updateTimeSlots={updateTimeSlots}
          selectionMode={selectionMode}
          selected={selected}
          onToggleSelect={toggleSelect}
        />
      ))}
    </Wrapper>
  )
})

export default TimesList
